import React, { useCallback, useEffect, useRef, useState } from "react";
import { Video, AlertTriangle, Download } from "lucide-react";

import { monitoringService } from "../../../api/monitoringService";
import { formatBytes, formatDuration, formatTime } from "../utils/format";

/**
 * Video playback for a session's chunks.
 *
 * A session is N independent chunk files, so this is a PLAYLIST, not a single
 * video. `onEnded` advances to the next chunk.
 *
 * Uses a plain `<video src>` against the Range-capable endpoint rather than a
 * blob download: a blob
 * forces the whole file down before the first frame and holds it in renderer
 * memory, which also defeats seeking. With a direct src the browser issues real
 * range requests, playback starts immediately, and scrubbing works.
 *
 * The cost of that is the token living in the URL, which brings two problems this
 * component has to handle: the browser reuses the same URL for every later range
 * request (so an expired token stalls playback mid-video), and a token is scoped to
 * one recording (so switching chunks needs a new one).
 */
const RecordingPlayer = ({ recordings }) => {
  const videoRef = useRef(null);
  const resumeRef = useRef(null);
  const issuedAtRef = useRef(0);
  const ttlRef = useRef(0);
  const refreshTimer = useRef(null);

  const [index, setIndex] = useState(0);
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);

  const current = recordings?.[index];

  const mint = useCallback(async (recordingId) => {
    const { token, ttlSeconds } =
      await monitoringService.createPlaybackToken(recordingId);
    issuedAtRef.current = Date.now();
    ttlRef.current = (ttlSeconds || 1800) * 1000;
    return monitoringService.getStreamUrl(recordingId, token);
  }, []);

  // Load (or reload) the current chunk.
  const load = useCallback(
    async (preservePosition = false) => {
      if (!current?._id) return;
      const video = videoRef.current;
      if (preservePosition && video) {
        resumeRef.current = { t: video.currentTime, playing: !video.paused };
      }
      try {
        setError(null);
        setSrc(await mint(current._id));
      } catch (err) {
        setError(
          err?.response?.data?.message || "Could not get a playback token",
        );
      }
    },
    [current, mint],
  );

  useEffect(() => {
    resumeRef.current = null;
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?._id]);

  // Re-mint at 80% of the token's life so a long watch never stalls. Without this
  // the browser would keep using a dead token for later range requests.
  useEffect(() => {
    if (!src) return undefined;
    clearTimeout(refreshTimer.current);
    const delay = Math.max(30_000, ttlRef.current * 0.8);
    refreshTimer.current = setTimeout(() => void load(true), delay);
    return () => clearTimeout(refreshTimer.current);
  }, [src, load]);

  const onLoadedMetadata = () => {
    const video = videoRef.current;
    const resume = resumeRef.current;
    if (video && resume) {
      video.currentTime = resume.t;
      if (resume.playing) void video.play().catch(() => {});
      resumeRef.current = null;
    }
  };

  const onError = () => {
    // A failure well after minting is most likely an expired token; retry once
    // with a fresh one before showing an error.
    if (Date.now() - issuedAtRef.current > 5000) void load(true);
    else setError("This chunk could not be played");
  };

  const download = async () => {
    if (!current?._id) return;
    try {
      const { token } = await monitoringService.createPlaybackToken(current._id);
      const a = document.createElement("a");
      a.href = monitoringService.getStreamUrl(current._id, token);
      a.download = `${current.sessionId}_${String(current.chunkIndex).padStart(5, "0")}.webm`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError("Could not start the download");
    }
  };

  if (!recordings || recordings.length === 0) {
    return (
      <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-8 text-center">
        <Video className="w-6 h-6 mx-auto text-gray-300 dark:text-dark-200 mb-2" />
        <p className="text-sm text-gray-500 dark:text-light-500">
          No video for this session
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Video className="w-4 h-4 text-primary-accent" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">
            Recording
          </h2>
          <span className="text-xs text-gray-400 dark:text-light-500 truncate">
            chunk {index + 1} of {recordings.length}
          </span>
        </div>
        <button
          onClick={download}
          className="p-1.5 rounded-lg text-gray-500 dark:text-light-400 hover:bg-gray-100 dark:hover:bg-dark-300 transition-colors"
          title="Download this chunk"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>

      <video
        ref={videoRef}
        key={current?._id}
        src={src ?? undefined}
        controls
        playsInline
        preload="metadata"
        onLoadedMetadata={onLoadedMetadata}
        onError={onError}
        onEnded={() => {
          if (index < recordings.length - 1) setIndex(index + 1);
        }}
        className="w-full bg-black aspect-video"
      />

      {error && (
        <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-xs text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {current && !current.remuxed && (
        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            This chunk was not re-muxed on the server, so seeking within it may
            not work. Playback from the start is unaffected.
          </span>
        </div>
      )}

      {/* Chunk strip. Pauses and recorder restarts show as gaps in the timeline,
          which is why each chunk is listed rather than one continuous scrubber. */}
      <div className="p-3 flex flex-wrap gap-1.5 border-t border-gray-200 dark:border-dark-100">
        {recordings.map((rec, i) => (
          <button
            key={rec._id}
            onClick={() => setIndex(i)}
            title={`${formatTime(rec.startedAt)} · ${formatDuration(rec.durationSeconds)} · ${formatBytes(rec.size)}`}
            className={`px-2 py-1 rounded text-[11px] tabular-nums transition-colors ${
              i === index
                ? "bg-primary-accent text-white"
                : "bg-gray-100 dark:bg-dark-200 text-gray-600 dark:text-light-400 hover:bg-gray-200 dark:hover:bg-dark-300"
            } ${rec.truncated ? "ring-1 ring-amber-400" : ""}`}
          >
            {formatTime(rec.startedAt)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default RecordingPlayer;
